import { useMemo } from 'react'
import { Loader2, ShieldCheck, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AuditTrailTable } from '@/components/audit/AuditTrailTable'
import { useSubmissionAuditTrail } from '@/hooks/useAuditTrail'
import { useAllowedSignatories } from '@/hooks/useSigners'
import { fieldAuditRows } from '@/lib/signatureAudit'

interface FieldRef {
  id: string
  label: string
}

/**
 * "View Allowed Signatories" (z8nrz7dt45 AC8): the groups a signature is
 * restricted to and the people currently in them — the same source the "Sign
 * as:" dropdown uses to mark people ineligible. Readable by anyone who can see
 * the field.
 */
export function AllowedSignatoriesDialog({
  submissionId,
  field,
  onClose,
}: {
  submissionId: string
  field: FieldRef | null
  onClose: () => void
}) {
  const query = useAllowedSignatories(submissionId, field?.id ?? null, field !== null)
  const data = query.data

  return (
    <Dialog open={field !== null} onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <ShieldCheck className='h-4 w-4 text-primary' /> Allowed Signatories
          </DialogTitle>
          <DialogDescription>Who may sign “{field?.label}”.</DialogDescription>
        </DialogHeader>

        {query.isLoading && (
          <p className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' /> Loading…
          </p>
        )}
        {query.isError && (
          <p className='text-sm text-destructive'>
            {query.error instanceof Error ? query.error.message : 'Could not load allowed signatories.'}
          </p>
        )}
        {data && !data.restricted && (
          <p className='text-sm text-muted-foreground'>
            This signature is not restricted to a group. Anyone who can edit this form may sign it.
          </p>
        )}
        {data?.distinctSet && (
          <div className='space-y-1 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm'>
            <p className='font-medium text-blue-900'>
              Distinct signers — set “{data.distinctSet.name}”
            </p>
            <p className='text-xs text-blue-800'>
              Must be signed by someone other than the signer of:
            </p>
            <ul className='text-xs text-blue-800'>
              {data.distinctSet.fields.map(f => (
                <li key={f.id}>
                  {f.label}: {f.signedBy ? <span className='font-semibold'>{f.signedBy}</span> : 'not signed yet'}
                </li>
              ))}
            </ul>
            {data.distinctSet.shortfall && (
              <p className='text-xs text-amber-700'>{data.distinctSet.shortfall}</p>
            )}
          </div>
        )}
        {data?.restricted && (
          <div className='space-y-4'>
            <div>
              <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Restricted to
              </p>
              <div className='flex flex-wrap gap-1.5'>
                {data.groups.map(g => (
                  <span key={g.id} className='rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'>
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className='mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                <Users className='h-3.5 w-3.5' /> People who may sign ({data.members.length})
              </p>
              {data.members.length === 0 ? (
                // z8nrz7dt45 AC9: say plainly that nobody can sign, rather than
                // leaving the operator to discover it at signing time.
                <p className='text-sm text-amber-700'>
                  No active user is in these groups, so nobody can sign this field right now.
                </p>
              ) : (
                <ul className='max-h-60 space-y-0.5 overflow-y-auto text-sm'>
                  {data.members.map(m => (
                    <li key={m.id}>{m.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * "View Audit Log" for one signature field: the submission's trail narrowed to
 * this field, so the whole signed → removed → re-signed sequence reads in one
 * place (z8nrz7dt40 AC7).
 */
export function FieldAuditLogDialog({
  submissionId,
  field,
  onClose,
}: {
  submissionId: string
  field: FieldRef | null
  onClose: () => void
}) {
  const trail = useSubmissionAuditTrail(submissionId, field !== null)
  const rows = useMemo(
    () => (field ? fieldAuditRows(trail.data ?? [], field.label) : []),
    [trail.data, field],
  )

  return (
    <Dialog open={field !== null} onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-4xl'>
        <DialogHeader>
          <DialogTitle>Audit Log</DialogTitle>
          <DialogDescription>Every recorded change to “{field?.label}”.</DialogDescription>
        </DialogHeader>
        {trail.isError && (
          <p className='text-sm text-destructive'>
            {trail.error instanceof Error ? trail.error.message : 'Failed to load the audit log.'}
          </p>
        )}
        <AuditTrailTable
          rows={rows}
          isLoading={trail.isLoading}
          emptyMessage='No changes recorded for this field yet.'
          tableId='signature-field-audit-log'
          maxHeightClass='max-h-[26rem]'
        />
      </DialogContent>
    </Dialog>
  )
}
